import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, Check, Phone, X, Fan, Snowflake, Wind, Box } from "lucide-react";
import { services, businessInfo } from "../data/mockData";

const iconMap = { Fan, Snowflake, Wind, Box };

const ServiceCard = ({ service, index, isVisible, onOpen }) => {
  const PlaceholderIcon = service.icon ? iconMap[service.icon] : null;
  return (
    <button
      type="button"
      onClick={() => onOpen(service)}
      className={`group relative overflow-hidden rounded-xl text-left w-full cursor-pointer transition-all duration-350 hover:shadow-2xl hover:-translate-y-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
      }`}
      style={{ transitionDelay: `${index * 50}ms` }}
      data-testid={`service-card-${service.id}`}
    >
      {/* Image or branded placeholder */}
      <div className="relative h-80 overflow-hidden">
        {service.image ? (
          <>
            <img loading="lazy" decoding="async"
              src={service.image}
              alt={service.title}
              className="w-full h-full object-cover transition-transform duration-350 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
          </>
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-brand-charcoal-dark via-brand-charcoal to-[#264676] flex items-center justify-center relative overflow-hidden">
            {/* subtle grid overlay */}
            <div
              aria-hidden="true"
              className="absolute inset-0 opacity-[0.08]"
              style={{
                backgroundImage:
                  "linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)",
                backgroundSize: "32px 32px",
              }}
            />
            {/* gold accent ring */}
            <div className="absolute -right-16 -bottom-16 w-64 h-64 rounded-full bg-brand-gold/10 blur-2xl" />
            {PlaceholderIcon && (
              <PlaceholderIcon
                className="w-24 h-24 text-white/85 transition-transform duration-350 group-hover:scale-110"
                strokeWidth={1.25}
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 p-6">
        <h3 className="font-heading text-xl text-white mb-1.5">
          {service.title}
        </h3>
        <p className="text-white/60 text-sm leading-relaxed max-h-0 group-hover:max-h-20 overflow-hidden transition-all duration-250 mb-2">
          {service.description}
        </p>
        <span
          className="inline-flex items-center gap-1.5 text-brand-gold text-xs font-bold uppercase tracking-wider group-hover:gap-2.5 transition-all duration-200 group-hover:text-brand-gold-light"
          data-testid={`service-learn-more-${service.id}`}
        >
          Learn More
          <ArrowRight className="w-3.5 h-3.5" />
        </span>
      </div>
    </button>
  );
};

const ServiceDetailModal = ({ service, onClose }) => {
  useEffect(() => {
    if (!service) return;
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "";
    };
  }, [service, onClose]);

  if (!service) return null;

  const scrollToContact = () => {
    onClose();
    setTimeout(() => {
      const el = document.querySelector("#contact");
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({ top, behavior: "smooth" });
      }
    }, 120);
  };

  return (
    <div
      className="fixed inset-0 bg-brand-charcoal-dark/85 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200"
      onClick={onClose}
      data-testid="service-detail-modal"
    >
      <div
        className="relative max-w-3xl w-full bg-white rounded-2xl overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Hero image / branded placeholder */}
        <div className="relative h-64 sm:h-72">
          {service.image ? (
            <>
              <img loading="lazy" decoding="async"
                src={service.image}
                alt={service.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-charcoal-dark via-brand-charcoal-dark/40 to-transparent" />
            </>
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-brand-charcoal-dark via-brand-charcoal to-[#264676] relative overflow-hidden">
              <div
                aria-hidden="true"
                className="absolute inset-0 opacity-[0.08]"
                style={{
                  backgroundImage:
                    "linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)",
                  backgroundSize: "32px 32px",
                }}
              />
              <div className="absolute -right-16 -bottom-16 w-64 h-64 rounded-full bg-brand-gold/10 blur-2xl" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-charcoal-dark via-brand-charcoal-dark/40 to-transparent" />
            </div>
          )}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center text-brand-charcoal hover:bg-white transition-colors shadow-lg"
            aria-label="Close"
            data-testid="service-modal-close"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="absolute bottom-0 left-0 right-0 p-7">
            <p className="text-brand-gold text-[10px] font-bold uppercase tracking-[0.25em] mb-2">
              Our Services
            </p>
            <h3 className="font-heading text-2xl sm:text-3xl text-white leading-tight">
              {service.title}
            </h3>
          </div>
        </div>

        {/* Body */}
        <div className="p-7 sm:p-9">
          <p className="text-gray-600 text-[15px] leading-[1.8] mb-7">
            {service.details.intro}
          </p>

          <p className="text-brand-charcoal text-xs font-bold uppercase tracking-[0.15em] mb-4">
            What's included
          </p>
          <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3 mb-8">
            {service.details.included.map((item) => (
              <li key={item} className="flex items-start gap-3">
                <span className="w-5 h-5 rounded-full bg-brand-mint/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-3 h-3 text-brand-charcoal" />
                </span>
                <span className="text-gray-600 text-sm leading-relaxed">
                  {item}
                </span>
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-100">
            <button
              onClick={scrollToContact}
              className="inline-flex items-center gap-2 bg-brand-charcoal hover:bg-brand-charcoal-dark text-white px-6 py-3 rounded-lg text-sm font-semibold tracking-wide transition-all duration-200 hover:shadow-lg"
              data-testid="service-modal-cta-quote"
            >
              Get a Free Quote
              <ArrowRight className="w-4 h-4" />
            </button>
            <a
              href={`tel:${businessInfo.phone}`}
              className="inline-flex items-center gap-2 border border-gray-200 hover:border-brand-charcoal text-brand-charcoal hover:bg-gray-50 px-6 py-3 rounded-lg text-sm font-semibold tracking-wide transition-all duration-200"
            >
              <Phone className="w-4 h-4" />
              {businessInfo.phone}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

const Services = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeService, setActiveService] = useState(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" ref={sectionRef} className="py-28 bg-gray-50/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-brand-mint-dark text-xs font-bold uppercase tracking-[0.25em] mb-4">
            Our Services
          </p>
          <h2 className="font-heading text-3xl lg:text-4xl text-gray-900 mb-4">
            Expert Air Conditioning Solutions
          </h2>
          <p className="text-gray-500 text-[15px] max-w-2xl mx-auto leading-relaxed">
            From single splits to whole-building systems — designed, installed
            and serviced across Yorkshire. Every job starts with a{" "}
            <span className="text-brand-charcoal font-semibold">
              free, no-obligation site survey and written quote
            </span>
            .
          </p>
        </div>

        {/* Service Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((service, index) => (
            <ServiceCard
              key={service.id}
              service={service}
              index={index}
              isVisible={isVisible}
              onOpen={setActiveService}
            />
          ))}
        </div>
      </div>

      <ServiceDetailModal
        service={activeService}
        onClose={() => setActiveService(null)}
      />
    </section>
  );
};

export default Services;
